/**
 * Reading a vehicle the way the game loads it, rather than the way it is filed.
 *
 * A working add-on car is an agreement between five files that never mention
 * each other. `vehicles.meta` names a `handlingId`; `handling.meta` is supposed
 * to define it. `carvariations.meta` names a `kitName`; `carcols.meta` is
 * supposed to define it. `vehicles.meta` names a `txdName`; a `.ytd` in
 * `stream/` is supposed to be called that. None of those links is checked by any
 * editor, none of them fails loudly, and every one of them fails the same way:
 * the resource starts, the file browser looks correct, and in game the car has
 * default physics, no mods, or no textures.
 *
 * That is why this module exists and why it is the largest one here. A file
 * browser shows contents; only a cross-reference shows agreement.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE NAIVE IMPLEMENTATION IS WRONG
 *
 * The obvious way to write this is to reuse an editor's meta parser and read the
 * fields off the objects it returns. That is exactly wrong, for two reasons the
 * repo has already been bitten by. An editor's parser reads the *first* `<Item>`
 * and stops, because it is editing one vehicle; and it merges what it read over
 * a defaults object, so an absent tag comes back holding a plausible value. Both
 * are right for an editor and fatal here — an analyzer that reads a default as
 * if the file said it invents references that do not exist and then reports them
 * as resolved. So every read in this module is presence-testing against the DOM,
 * and an absent tag is `null`. Never a default. Never a guess.
 *
 * The second way to get this wrong is to treat every name that does not resolve
 * inside this folder as broken. `audioNameHash POLICE`, `LAYOUT_STANDARD`, siren
 * profile 7 and half of the base game's mod kits are all defined by GTA V, not
 * by the resource being read. A linter that flagged those would cry wolf on
 * every correct vehicle in existence. `desktop/src/lib/resourceScan.ts` settled
 * that judgement already and this module preserves it: a reference that points
 * outside the folder is external, not missing, and when the file that would have
 * resolved it was never loaded the check is recorded in `skipped` rather than
 * silently passed.
 *
 * The third is the high-LOD suffixes. Models use `_hi`, texture dictionaries use
 * `+hi`, that asymmetry is Rockstar's own, and a tool that normalizes one and
 * not the other reports half of every correct vehicle as broken. The helpers in
 * `normalize.ts` own that rule, and the `+` never reaches a `RegExp` unescaped.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS
 *
 * Source positions are derived, not parsed. DOM nodes carry no offsets, so a
 * finding's line and column come from searching the source text for the
 * element's identifying string — the `modelName` value, then the offending tag
 * scanned forward from there, which keeps duplicates apart because the items are
 * searched in document order. When that search finds nothing the finding carries
 * the file and no line, because a wrong line number is worse than none.
 *
 * Nothing here reads a file, hashes a model, or opens an archive. The caller
 * supplies decoded text, the set of stream file names, and — only if it chose to
 * pay for it — the vanilla asset name index. Absent inputs are absent checks,
 * and the report says which.
 */

import { joaat } from "../freeTools/joaat";
import { isEmergencyStockModkitId, isEmergencyVehicleName } from "./data/emergencyVehicles.v1";
import { VANILLA_LIGHT_IDS, VANILLA_SIREN_IDS } from "./data/vanillaModkits.v1";
import { VANILLA_VEHICLE_MODELS } from "./data/vanillaVehicles.v1";
import { isDefaultModkitName, isStockModkitId } from "./modkits";
import {
  extensionOf,
  highLodNames,
  invalidSpawnNameChars,
  isValidAudioName,
  isValidSpawnName,
  normalizeName,
  positionAt,
  stemOf,
} from "./normalize";
import type {
  GtaFinding,
  GtaFindingTag,
  GtaSeverity,
  GtaSourceSpan,
  VanillaAssetNameIndex,
  VehicleEntity,
} from "./types";

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * A `.meta` this large is not a `.meta`. Parsing one would stall the thread the
 * desktop analysis view and PackBench's preflight both run on, so it is refused
 * and reported as unreadable rather than attempted.
 */
const MAX_META_BYTES = 32 * 1024 * 1024;

/**
 * Cap the files parsed per resource, so the analysis view cannot be hung by a
 * folder holding thousands of them. Tripping it is reported in `skipped`.
 */
const MAX_META_FILES = 400;

/**
 * Cap the Lua files scanned for `AddTextEntry`, which protects the same two
 * surfaces: PackBench's export preflight and the desktop analysis view both run
 * this pass on the thread that has to keep painting.
 */
const MAX_LUA_FILES = 400;

/**
 * Cap the `<Item>` rows read from one file — the vehicle list in the report UI
 * is a list, not a database.
 */
const MAX_ITEMS_PER_FILE = 5000;

/** Cap the kits one vehicle can claim, so a pasted-in file cannot flood a single vehicle card. */
const MAX_KITS_PER_VEHICLE = 128;

/** Cap the flags kept per vehicle; the vehicle card shows them as chips. */
const MAX_FLAGS_PER_VEHICLE = 256;

/** Cap the `AddTextEntry` calls read across a resource, protecting the same preflight pass. */
const MAX_TEXT_ENTRIES = 50000;

// ─── Meta kinds ──────────────────────────────────────────────────────────────

type MetaKind =
  | "vehicles"
  | "handling"
  | "carvariations"
  | "carcols"
  | "vehiclelayouts"
  | "unknown";

/** The XML root each vehicle meta kind must have, which is how the kind is known. */
const ROOT_ELEMENTS: readonly (readonly [string, MetaKind])[] = [
  ["CVehicleModelInfo__InitDataList", "vehicles"],
  ["CHandlingDataMgr", "handling"],
  ["CVehicleModelInfoVariation", "carvariations"],
  ["CVehicleModelInfoVarGlobal", "carcols"],
  ["CVehicleMetadataMgr", "vehiclelayouts"],
];

/**
 * Words that make a file our business even when it does not parse.
 *
 * A resource holds plenty of XML this module has no opinion about, and reporting
 * `settings.xml` as an unrecognised vehicle file would be noise. A file whose
 * name carries one of these words is one the author meant as a vehicle meta, so
 * a parse failure or an unexpected root is worth saying out loud.
 */
const VEHICLE_META_WORDS: readonly string[] = [
  "vehicles",
  "handling",
  "carcols",
  "carvariations",
  "vehiclelayouts",
];

// ─── Public input and output ─────────────────────────────────────────────────

export type VehicleMetaInput = {
  /** Decoded text by lower-cased relative path, as GtaResourceInput.texts. */
  texts: Map<string, string>;
  /** Lower-cased "name.extension" of every file under a stream folder. */
  streamNames?: Set<string>;
  /** Present only when the caller loaded it. Absent means the check is skipped, not passed. */
  vanillaNames?: VanillaAssetNameIndex;
};

export type VehicleMetaResult = {
  vehicles: VehicleEntity[];
  findings: GtaFinding[]; // resource-level only; per-vehicle ones live on the vehicle
  skipped: string[];
};

// ─── XML ─────────────────────────────────────────────────────────────────────

/**
 * Parse a `.meta` and say plainly when it is not XML.
 *
 * A byte-order mark and leading whitespace before the declaration are both
 * common in hand-edited files and both make a strict parser refuse, so they are
 * trimmed first — the game tolerates them and so should the report. Everything
 * else that fails, fails to `null`: this never throws.
 */
export function parseMetaXml(text: string): Document | null {
  if (typeof text !== "string" || text.length === 0) return null;
  if (text.length > MAX_META_BYTES) return null;
  try {
    const doc = new DOMParser().parseFromString(
      text.replace(/^\uFEFF/, "").replace(/^\s+<\?xml/i, "<?xml"),
      "application/xml",
    );
    if (!doc || !doc.documentElement) return null;
    return doc.querySelector("parsererror") ? null : doc;
  } catch {
    return null;
  }
}

/**
 * Which of the five vehicle files this document is, by its root element.
 *
 * The root is checked first and then searched for, because a resource that wraps
 * its data in an outer element still loads in game and should still be read
 * here. Anything else is `unknown`, which is a finding rather than a crash.
 */
export function metaKindOf(doc: Document): MetaKind {
  const root = doc?.documentElement;
  if (!root) return "unknown";
  for (const [tag, kind] of ROOT_ELEMENTS) {
    if (root.tagName === tag) return kind;
  }
  for (const [tag, kind] of ROOT_ELEMENTS) {
    if (doc.querySelector(tag)) return kind;
  }
  return "unknown";
}

/** A direct child by tag name. Element-by-element, so no tag name has to survive CSS escaping. */
function firstChild(element: Element | null, tag: string): Element | null {
  if (!element) return null;
  for (const child of Array.from(element.children)) {
    if (child.tagName === tag) return child;
  }
  return null;
}

/**
 * A direct child's `value` attribute or text, trimmed.
 *
 * An absent tag and an empty one both come back `null`, which is the whole point
 * of this function: the caller that needs to tell them apart asks `hasChild`,
 * and every other caller is protected from inventing a value the file never
 * stated.
 */
function childText(element: Element | null, tag: string): string | null {
  const child = firstChild(element, tag);
  if (!child) return null;
  const raw = (child.getAttribute("value") ?? child.textContent ?? "").trim();
  return raw === "" ? null : raw;
}

/** Whether the tag is there at all, regardless of what it holds. */
function hasChild(element: Element | null, tag: string): boolean {
  return firstChild(element, tag) !== null;
}

/** `root > container > Item`, tolerating a wrapped or renamed root. */
function itemsUnder(doc: Document, root: string, container: string): Element[] {
  const rootEl =
    doc.documentElement?.tagName === root ? doc.documentElement : doc.querySelector(root);
  const scope = rootEl ?? doc.documentElement;
  if (!scope) return [];
  const containerEl = firstChild(scope, container) ?? scope.querySelector(container);
  if (!containerEl) return [];
  const items: Element[] = [];
  for (const child of Array.from(containerEl.children)) {
    if (child.tagName !== "Item") continue;
    items.push(child);
    if (items.length >= MAX_ITEMS_PER_FILE) break;
  }
  return items;
}

// ─── The five parsers ────────────────────────────────────────────────────────

/**
 * Every vehicle a `vehicles.meta` declares, in document order.
 *
 * An item with no `modelName` is kept, with an empty `model`, because dropping
 * it would silently lose the one finding that matters most about it: a vehicle
 * the game cannot spawn at all.
 */
export function parseVehiclesMeta(doc: Document): {
  model: string;
  modelRaw: string;
  txdName: string | null;
  handlingId: string | null;
  gameName: string | null;
  vehicleMakeName: string | null;
  audioNameHash: string | null;
  layout: string | null;
  flags: string[];
  element: Element;
}[] {
  return itemsUnder(doc, "CVehicleModelInfo__InitDataList", "InitDatas").map((element) => {
    const modelRaw = childText(element, "modelName") ?? "";
    const flags = (childText(element, "flags") ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, MAX_FLAGS_PER_VEHICLE);
    return {
      model: normalizeName(modelRaw),
      modelRaw,
      txdName: childText(element, "txdName"),
      handlingId: childText(element, "handlingId"),
      gameName: childText(element, "gameName"),
      vehicleMakeName: childText(element, "vehicleMakeName"),
      audioNameHash: childText(element, "audioNameHash"),
      layout: childText(element, "layout"),
      flags,
      element,
    };
  });
}

/** Every handling entry a `handling.meta` defines. An entry with no name keeps an empty one. */
export function parseHandlingMeta(doc: Document): { handlingName: string; element: Element }[] {
  return itemsUnder(doc, "CHandlingDataMgr", "HandlingData").map((element) => ({
    handlingName: childText(element, "handlingName") ?? "",
    element,
  }));
}

/** Every variation a `carvariations.meta` defines, with the kits and profiles it claims. */
export function parseCarvariationsMeta(doc: Document): {
  model: string;
  kits: string[];
  sirenSettings: string | null;
  lightSettings: string | null;
  element: Element;
}[] {
  return itemsUnder(doc, "CVehicleModelInfoVariation", "variationData").map((element) => {
    const kitsEl = firstChild(element, "kits");
    const kits: string[] = [];
    for (const item of Array.from(kitsEl?.children ?? [])) {
      if (item.tagName !== "Item") continue;
      const name = (item.getAttribute("value") ?? item.textContent ?? "").trim();
      if (name !== "") kits.push(name);
      if (kits.length >= MAX_KITS_PER_VEHICLE) break;
    }
    return {
      model: normalizeName(childText(element, "modelName") ?? ""),
      kits,
      sirenSettings: childText(element, "sirenSettings"),
      lightSettings: childText(element, "lightSettings"),
      element,
    };
  });
}

/**
 * The kits, siren profiles and light settings a `carcols.meta` defines.
 *
 * The `id` is read only when the file states one. The leading number in a kit
 * name is a convention and Rockstar's own files disagree with it, so inferring
 * an id from a name is a guess the caller has to opt into knowingly — it is not
 * done here.
 */
export function parseCarcolsMeta(doc: Document): {
  kits: { kitName: string; id: number | null; element: Element }[];
  sirenIds: string[];
  lightIds: string[];
} {
  const kits = itemsUnder(doc, "CVehicleModelInfoVarGlobal", "Kits").map((element) => {
    // Whole-string integers only. `parseInt` would read `16abc` as 16 and the
    // report would then blame a stock-id collision on a kit whose id the game
    // never manages to read at all.
    const raw = childText(element, "id");
    const numeric = raw !== null && /^-?\d+$/.test(raw);
    return {
      kitName: childText(element, "kitName") ?? "",
      id: numeric ? Number.parseInt(raw as string, 10) : null,
      element,
    };
  });

  const idsUnder = (container: string): string[] => {
    const out: string[] = [];
    for (const item of itemsUnder(doc, "CVehicleModelInfoVarGlobal", container)) {
      const id = childText(item, "id");
      if (id !== null) out.push(id);
    }
    return out;
  };

  return { kits, sirenIds: idsUnder("Sirens"), lightIds: idsUnder("Lights") };
}

/** Every seat layout name a `vehiclelayouts.meta` defines, as written. */
export function parseVehicleLayoutsMeta(doc: Document): string[] {
  const out: string[] = [];
  const items = Array.from(doc?.querySelectorAll("VehicleLayoutInfos > Item > Name") ?? []);
  for (const el of items) {
    const name = (el.getAttribute("value") ?? el.textContent ?? "").trim();
    if (name !== "") out.push(name);
    if (out.length >= MAX_ITEMS_PER_FILE) break;
  }
  return out;
}

// ─── Text entries ────────────────────────────────────────────────────────────

/**
 * Lua comments blanked out, so a commented-out registration is not read as one.
 *
 * Quote-aware on purpose. A regex that deletes from every `--` to the end of the
 * line also eats the middle of `AddTextEntry('X', 'Ambulance -- Fire')`, the
 * registration then stops matching, and the report claims a display name that
 * works in game never resolves. Comment bodies are replaced with spaces rather
 * than removed so that every surviving offset still lines up with the original.
 */
function stripLuaComments(source: string): string {
  const out = source.split("");
  let quote: string | null = null;
  let i = 0;
  while (i < source.length) {
    const char = source[i];
    if (quote !== null) {
      if (char === "\\") {
        i += 2;
        continue;
      }
      if (char === quote) quote = null;
      i += 1;
      continue;
    }
    if (char === "'" || char === '"') {
      quote = char;
      i += 1;
      continue;
    }
    if (char === "-" && source[i + 1] === "-") {
      // `--[[ … ]]` runs to its terminator; a plain `--` runs to the newline.
      const long = source.startsWith("--[[", i);
      const end = long
        ? (() => {
            const close = source.indexOf("]]", i + 4);
            return close === -1 ? source.length : close + 2;
          })()
        : (() => {
            const newline = source.indexOf("\n", i);
            return newline === -1 ? source.length : newline;
          })();
      for (let j = i; j < end; j += 1) {
        if (out[j] !== "\n") out[j] = " ";
      }
      i = end;
      continue;
    }
    i += 1;
  }
  return out.join("");
}

/** `AddTextEntry('0x488AD9F6', 'SPACE')` → the label keys a resource registers. */
export function parseTextEntries(source: string): { key: string; text: string }[] {
  const out: { key: string; text: string }[] = [];
  if (typeof source !== "string" || source.length === 0) return out;
  for (const match of stripLuaComments(source).matchAll(
    /AddTextEntry\s*\(\s*(['"])([^'"]*)\1\s*,\s*(['"])([\s\S]*?)\3\s*\)/g,
  )) {
    out.push({ key: match[2], text: match[4] });
    // Capped for the same reason every loop over user data here is: this runs
    // inside PackBench's export preflight, on the UI thread.
    if (out.length >= MAX_TEXT_ENTRIES) break;
  }
  return out;
}

/**
 * The 32-bit key an `AddTextEntry` argument registers under.
 *
 * FiveM accepts either a literal `0x…` hash or a plain string it hashes for you,
 * and mod authors use both. Reading only one form would make the display-name
 * check report false failures on half the resources in the wild.
 */
export function textEntryHash(entryKey: string): number {
  if (typeof entryKey !== "string") return 0;
  const hex = /^0x([0-9a-f]{1,8})$/i.exec(entryKey.trim());
  return hex ? Number.parseInt(hex[1], 16) >>> 0 : joaat(entryKey);
}

export function toHex(hash: number): string {
  return `0x${(Number.isFinite(hash) ? hash >>> 0 : 0).toString(16).toUpperCase().padStart(8, "0")}`;
}

// ─── Source positions ────────────────────────────────────────────────────────

/**
 * Every regex-special character escaped, `+` very much included.
 *
 * `police+hi.ytd` is a real texture dictionary name. Dropped into a pattern
 * unescaped it becomes "one or more `e`", matches nothing, and the high-detail
 * dictionary of every correct vehicle reads as absent.
 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Where the *value* of a tag sits in the source, searching forward.
 *
 * The offset points at the value rather than at the `<` before it, because that
 * is what a reader is being sent to look at and what a later autofix would have
 * to replace: an offset on the opening tag makes `byteStart`/`byteEnd` describe
 * `<modelNam` instead of `police`, and every edit built from it lands short.
 *
 * Forward from a caller-held cursor rather than from zero, because two vehicles
 * in one file legitimately share a `txdName` and the second one's finding must
 * not point at the first one's line. Returns -1 when the text cannot be found,
 * and the caller then reports the file without a line.
 */
function tagOffset(source: string, tag: string, value: string, from = 0): number {
  if (value === "") return -1;
  const name = escapeRegExp(tag);
  const text = escapeRegExp(value);
  // Group 1 is everything before the value, so its length is the distance from
  // the match to the value itself. Both spellings are here because meta files
  // write scalars as element text and as a `value` attribute interchangeably.
  const patterns = [
    new RegExp(`(<${name}\\b[^>]*>\\s*)${text}\\s*</`, "i"),
    new RegExp(`(<${name}\\b[^>]*value\\s*=\\s*["'])${text}["']`, "i"),
  ];
  const start = Math.max(0, from);
  const window = source.slice(start);
  for (const pattern of patterns) {
    const match = pattern.exec(window);
    if (match) return start + match.index + match[1].length;
  }
  return -1;
}

function spanAt(file: string, source: string, offset: number, length: number): GtaSourceSpan {
  if (offset < 0) return { file };
  const { line, column } = positionAt(source, offset);
  return { file, line, column, byteStart: offset, byteEnd: offset + length };
}

// ─── Loaded files ────────────────────────────────────────────────────────────

type LoadedMeta = { file: string; source: string; doc: Document; kind: MetaKind };

/** Whether the file name says the author meant this to be one of the vehicle metas. */
function looksLikeVehicleMeta(path: string): boolean {
  const stem = stemOf(path).toLowerCase();
  return VEHICLE_META_WORDS.some((word) => stem.includes(word));
}

/** How a character reads in a sentence, so a space is not printed as nothing. */
function describeChar(char: string): string {
  if (char === " ") return "a space";
  if (char === "\t") return "a tab";
  if (char === "\n" || char === "\r") return "a line break";
  return `"${char}"`;
}

/**
 * "a space, \"-\" and \".\"" — the offending characters, named exactly.
 *
 * The empty case is guarded because the alternative is a finding whose detail
 * reads "It contains undefined", which teaches the reader nothing and destroys
 * their trust in every other sentence in the report.
 */
function listChars(chars: string[]): string {
  const described = chars.map(describeChar);
  if (described.length === 0) return "characters the game's name hash cannot represent";
  if (described.length === 1) return described[0];
  return `${described.slice(0, -1).join(", ")} and ${described[described.length - 1]}`;
}

/**
 * The one spelling a siren or light profile id is compared under.
 *
 * `carcols.meta` writes `<id value="1"/>` and a hand-edited `carvariations.meta`
 * happily writes `01`; the game reads both into the same integer field, so a
 * string comparison would report a profile that exists as missing and send the
 * author looking for a bug in a file that is correct.
 */
function profileIdKey(value: string): string {
  const trimmed = value.trim();
  return /^-?\d+$/.test(trimmed) ? String(Number.parseInt(trimmed, 10)) : normalizeName(trimmed);
}

/**
 * The stock lookups, built on first use and never at import time.
 *
 * There are 935 stock spawn names and a couple of hundred profile ids, and the
 * browser bundles that run these checks are the same ones that have to paint a
 * first frame. A module that builds three sets as a side effect of being
 * imported charges every page load for a check most sessions never run, so this
 * follows the same lazy discipline `modkits.ts` uses for the stock kit table.
 */
let cachedVanillaModels: ReadonlySet<string> | null = null;
let cachedVanillaSirenIds: ReadonlySet<string> | null = null;
let cachedVanillaLightIds: ReadonlySet<string> | null = null;

function vanillaModelSet(): ReadonlySet<string> {
  if (!cachedVanillaModels) cachedVanillaModels = new Set(VANILLA_VEHICLE_MODELS);
  return cachedVanillaModels;
}

function vanillaSirenIdSet(): ReadonlySet<string> {
  if (!cachedVanillaSirenIds) cachedVanillaSirenIds = new Set(VANILLA_SIREN_IDS.map(String));
  return cachedVanillaSirenIds;
}

function vanillaLightIdSet(): ReadonlySet<string> {
  if (!cachedVanillaLightIds) cachedVanillaLightIds = new Set(VANILLA_LIGHT_IDS.map(String));
  return cachedVanillaLightIds;
}

/** Errors first, then the police car ahead of the sedan, then alphabetical by title. */
function sortFindings(findings: GtaFinding[]): GtaFinding[] {
  const rank: Record<GtaSeverity, number> = { error: 0, warning: 1, info: 2 };
  return [...findings].sort((a, b) => {
    const bySeverity = rank[a.severity] - rank[b.severity];
    if (bySeverity !== 0) return bySeverity;
    const emergency = Number(b.tags?.includes("emergency-impact") ?? false) -
      Number(a.tags?.includes("emergency-impact") ?? false);
    if (emergency !== 0) return emergency;
    return a.title.localeCompare(b.title);
  });
}

// ─── The analysis ────────────────────────────────────────────────────────────

/**
 * Cross-reference every vehicle file in a resource and report what dangles.
 *
 * The result is grouped by vehicle on purpose. One misspelled `handlingId`
 * produces a single vehicle carrying the finding and its consequences, not nine
 * file-shaped errors the reader has to reassemble.
 */
export function analyzeVehicleMeta(input: VehicleMetaInput): VehicleMetaResult {
  const findings: GtaFinding[] = [];
  const skipped: string[] = [];
  const vehicles: VehicleEntity[] = [];

  const texts = input.texts ?? new Map<string, string>();
  const streamNames = input.streamNames;
  const vanillaNames = input.vanillaNames;

  // ── Load and classify every candidate file ────────────────────────────────

  const loaded: LoadedMeta[] = [];
  let filesSeen = 0;
  let filesRefused = 0;
  for (const [path, source] of texts) {
    const extension = extensionOf(path);
    if (extension !== "meta" && extension !== "xml") continue;
    filesSeen += 1;
    if (filesSeen > MAX_META_FILES) {
      filesRefused += 1;
      continue;
    }

    const named = looksLikeVehicleMeta(path);

    // Refused before parsing rather than after, and with its own sentence: a
    // 32 MB "meta" is not a broken tag, and telling the author to go hunting
    // for one wastes an afternoon.
    if (typeof source === "string" && source.length > MAX_META_BYTES) {
      if (named) {
        findings.push({
          code: "vehicle.xml.invalid",
          severity: "warning",
          title: `${path} is too large to read`,
          detail:
            "This file is bigger than any real vehicle meta, so it was not parsed and nothing in " +
            "it was cross-referenced — the vehicles or kits it may define are absent from this " +
            "report rather than confirmed correct. Check that the file is what its name says.",
          file: path,
          entity: { kind: "resource", id: path },
        });
      }
      continue;
    }

    const doc = parseMetaXml(source);
    if (!doc) {
      if (named) {
        findings.push({
          code: "vehicle.xml.invalid",
          severity: "error",
          title: `${path} is not readable as XML`,
          detail:
            "The game's meta loader stops at the first malformed tag, so nothing in this file is " +
            "applied — the vehicles, handling entries or kits it defines simply do not exist at " +
            "runtime. Open it and look for an unclosed tag or a stray character near the end.",
          file: path,
          entity: { kind: "resource", id: path },
        });
      }
      continue;
    }

    const kind = metaKindOf(doc);
    if (kind === "unknown") {
      if (named) {
        findings.push({
          code: "vehicle.xml.unknown_kind",
          severity: "warning",
          title: `${path} has an unexpected root element`,
          detail:
            `Its root is <${doc.documentElement.tagName}>, which is not one of the five vehicle ` +
            "meta roots. The game reads a data file by the type the manifest declares, so a file " +
            "with the wrong root is loaded and then ignored, and nothing in it takes effect. " +
            "Check that the file's contents match the name it was given.",
          file: path,
          entity: { kind: "resource", id: path },
        });
      }
      continue;
    }

    loaded.push({ file: path, source, doc, kind });
  }

  const filesOfKind = (kind: MetaKind): LoadedMeta[] => loaded.filter((file) => file.kind === kind);
  const vehicleFiles = filesOfKind("vehicles");
  const handlingFiles = filesOfKind("handling");
  const carvariationFiles = filesOfKind("carvariations");
  const carcolsFiles = filesOfKind("carcols");
  const layoutFiles = filesOfKind("vehiclelayouts");

  // ── Handling ──────────────────────────────────────────────────────────────

  type HandlingRecord = { name: string; file: string; source: string; offset: number };
  const handlingEntries: HandlingRecord[] = [];
  const handlingByName = new Map<string, HandlingRecord>();

  for (const file of handlingFiles) {
    let cursor = 0;
    for (const entry of parseHandlingMeta(file.doc)) {
      if (entry.handlingName === "") {
        findings.push({
          code: "vehicle.handling.missing",
          severity: "error",
          title: "A handling entry has no handlingName",
          detail:
            "An entry with no name cannot be pointed at, so the physics it describes are loaded " +
            "into memory and never used by any vehicle. Give it the handlingId the vehicle asks " +
            "for, or delete it.",
          file: file.file,
          entity: { kind: "resource", id: file.file },
        });
        continue;
      }
      const offset = tagOffset(file.source, "handlingName", entry.handlingName, cursor);
      if (offset >= 0) cursor = offset + 1;
      const record: HandlingRecord = {
        name: normalizeName(entry.handlingName),
        file: file.file,
        source: file.source,
        offset,
      };
      handlingEntries.push(record);

      const existing = handlingByName.get(record.name);
      if (existing) {
        findings.push({
          code: "vehicle.handling.duplicate",
          severity: "error",
          title: `handlingName ${entry.handlingName} is defined twice`,
          detail:
            "The game keeps the first definition it reads and discards the rest, so whichever set " +
            "of physics values comes second is dead weight and the vehicle drives like the other " +
            "one. Rename one of them, or delete the copy.",
          ...spanAt(file.file, file.source, offset, entry.handlingName.length),
          entity: { kind: "vehicle", id: record.name },
        });
      } else {
        handlingByName.set(record.name, record);
      }
    }
  }

  // ── Layouts ───────────────────────────────────────────────────────────────

  const layoutNames = new Set<string>();
  for (const file of layoutFiles) {
    for (const name of parseVehicleLayoutsMeta(file.doc)) layoutNames.add(normalizeName(name));
  }

  // ── Mod kits, sirens and lights ───────────────────────────────────────────

  type KitRecord = {
    kitName: string;
    key: string;
    id: number | null;
    idInferred: boolean;
    file: string;
    source: string;
    offset: number;
  };
  const kitRecords: KitRecord[] = [];
  const kitsByName = new Map<string, KitRecord>();
  const sirenIds = new Set<string>();
  const lightIds = new Set<string>();

  for (const file of carcolsFiles) {
    const parsed = parseCarcolsMeta(file.doc);
    let cursor = 0;
    for (const kit of parsed.kits) {
      if (kit.kitName === "") continue;
      const offset = tagOffset(file.source, "kitName", kit.kitName, cursor);
      if (offset >= 0) cursor = offset + 1;
      // The leading number in a kit name is only a convention, so an id read
      // from it is flagged as inferred and never reported with the confidence
      // of one the file actually stated.
      const fromName = /^(\d+)_/.exec(kit.kitName);
      const record: KitRecord = {
        kitName: kit.kitName,
        key: normalizeName(kit.kitName),
        id: kit.id ?? (fromName ? Number.parseInt(fromName[1], 10) : null),
        idInferred: kit.id === null && fromName !== null,
        file: file.file,
        source: file.source,
        offset,
      };
      kitRecords.push(record);
      if (!kitsByName.has(record.key)) kitsByName.set(record.key, record);
    }
    for (const id of parsed.sirenIds) sirenIds.add(profileIdKey(id));
    for (const id of parsed.lightIds) lightIds.add(profileIdKey(id));
  }

  // ── Variations ────────────────────────────────────────────────────────────

  type VariationRecord = {
    model: string;
    kits: string[];
    sirenSettings: string | null;
    lightSettings: string | null;
    file: string;
    source: string;
    offset: number;
  };
  const variations = new Map<string, VariationRecord>();

  for (const file of carvariationFiles) {
    let cursor = 0;
    for (const entry of parseCarvariationsMeta(file.doc)) {
      if (entry.model === "") continue;
      const offset = tagOffset(file.source, "modelName", entry.model, cursor);
      if (offset >= 0) cursor = offset + 1;
      if (!variations.has(entry.model)) {
        variations.set(entry.model, {
          model: entry.model,
          kits: entry.kits,
          sirenSettings: entry.sirenSettings,
          lightSettings: entry.lightSettings,
          file: file.file,
          source: file.source,
          offset,
        });
      }
    }
  }

  // ── Registered labels ─────────────────────────────────────────────────────

  const registeredLabels = new Map<number, string>();
  let luaFilesSeen = 0;
  let luaFilesRefused = 0;
  let labelCapReached = false;
  for (const [path, source] of texts) {
    if (extensionOf(path) !== "lua") continue;
    luaFilesSeen += 1;
    if (luaFilesSeen > MAX_LUA_FILES || labelCapReached) {
      luaFilesRefused += 1;
      continue;
    }
    for (const entry of parseTextEntries(source)) {
      registeredLabels.set(textEntryHash(entry.key), entry.text);
      if (registeredLabels.size >= MAX_TEXT_ENTRIES) {
        labelCapReached = true;
        break;
      }
    }
  }

  // ── What could not be checked ─────────────────────────────────────────────

  if (vehicleFiles.length === 0) {
    skipped.push(
      "No vehicles.meta was loaded, so no vehicle was cross-referenced against its handling, " +
        "variations or stream files.",
    );
  }
  if (handlingFiles.length === 0) {
    skipped.push(
      "No handling.meta was loaded, so every handlingId was treated as physics defined by another " +
        "resource rather than as a broken reference.",
    );
  }
  if (carvariationFiles.length === 0) {
    skipped.push(
      "No carvariations.meta was loaded, so no vehicle's mod kits, siren profile or light " +
        "settings were checked.",
    );
  }
  if (carcolsFiles.length === 0) {
    skipped.push(
      "No carcols.meta was loaded, so mod kit names could not be verified and no mod kit id was " +
        "checked against the base game's.",
    );
  }
  if (layoutFiles.length === 0) {
    skipped.push(
      "No vehiclelayouts.meta was loaded, so every layout was treated as a base-game seat layout.",
    );
  }
  if (!streamNames) {
    skipped.push(
      "No stream file list was supplied, so no model or texture dictionary was checked for a " +
        "matching file.",
    );
  }
  if (!vanillaNames) {
    skipped.push(
      "No vanilla asset name index was supplied, so no name was checked against the base game's " +
        "own streamed assets.",
    );
  }
  if (registeredLabels.size === 0) {
    skipped.push(
      "No AddTextEntry call was found in any Lua file, so no gameName was checked against a " +
        "registered label.",
    );
  }
  if (filesRefused > 0) {
    skipped.push(
      `This folder holds more than ${MAX_META_FILES} meta or XML files, so ${filesRefused} of ` +
        "them were not read at all and anything they define is missing from this report rather " +
        "than confirmed correct.",
    );
  }
  if (luaFilesRefused > 0) {
    skipped.push(
      `${luaFilesRefused} Lua files were not scanned for AddTextEntry, so a display name they ` +
        "register may be reported as unresolved when it is in fact fine.",
    );
  }

  // ── Every vehicle ─────────────────────────────────────────────────────────

  const seenModels = new Map<string, string>();
  const referencedHandling = new Set<string>();
  const kitUsers = new Map<string, string[]>();
  let makesSeen = false;

  for (const file of vehicleFiles) {
    let cursor = 0;
    for (const row of parseVehiclesMeta(file.doc)) {
      const offset = row.modelRaw === "" ? -1 : tagOffset(file.source, "modelName", row.modelRaw, cursor);
      if (offset >= 0) cursor = offset + 1;
      const span = spanAt(file.file, file.source, offset, row.modelRaw.length);
      const emergency = isEmergencyVehicleName(row.model).emergency;

      const vehicle: VehicleEntity = {
        model: row.model,
        modelRaw: row.modelRaw,
        gameName: row.gameName,
        txdName: row.txdName,
        handlingId: row.handlingId,
        layout: row.layout,
        audioNameHash: row.audioNameHash,
        modkits: [],
        sirenSettings: null,
        lightSettings: null,
        flags: row.flags,
        emergency,
        span,
        findings: [],
      };
      vehicles.push(vehicle);

      /** Attach a finding to this vehicle, badging it if the fleet notices. */
      const report = (finding: GtaFinding): void => {
        const tags = new Set<GtaFindingTag>(finding.tags ?? []);
        if (emergency) tags.add("emergency-impact");
        vehicle.findings.push({
          ...finding,
          entity: finding.entity ?? { kind: "vehicle", id: row.model || file.file },
          tags: tags.size > 0 ? [...tags] : undefined,
        });
      };

      /** The position of one of this vehicle's own tags, or the vehicle's own line. */
      const tagSpan = (tag: string, value: string): GtaSourceSpan => {
        const at = tagOffset(file.source, tag, value, Math.max(0, offset));
        return at >= 0 ? spanAt(file.file, file.source, at, value.length) : span;
      };

      // Spawn name.
      if (row.modelRaw === "") {
        report({
          code: "vehicle.model.missing",
          severity: "error",
          title: "A vehicle entry has no modelName",
          detail:
            "The spawn name is the only handle the game has on a vehicle: with no modelName the " +
            "entry is unspawnable and every other field in it is inert. Add the spawn name the " +
            "model file is called.",
          ...span,
        });
      } else if (!isValidSpawnName(row.modelRaw)) {
        const bad = invalidSpawnNameChars(row.modelRaw);
        report({
          code: "vehicle.model.invalid_chars",
          severity: "error",
          title: `The spawn name ${row.modelRaw} contains characters the game cannot use`,
          detail:
            `It contains ${listChars(bad)}. The engine hashes a spawn name and looks the hash up; ` +
            "it does not reject the name, it produces a hash nothing matches, so the vehicle " +
            "silently does not exist. Use only letters, digits and underscores.",
          ...span,
        });
      }

      if (row.model !== "") {
        const previous = seenModels.get(row.model);
        if (previous) {
          report({
            code: "vehicle.model.duplicate",
            severity: "error",
            title: `${row.modelRaw} is declared twice`,
            detail:
              `Another entry (in ${previous}) already claims this spawn name. The game keeps one ` +
              "of them and discards the other, so half of what was authored here never appears in " +
              "game and which half is not predictable. Rename one of the two vehicles.",
            ...span,
          });
        } else {
          seenModels.set(row.model, file.file);
        }

        if (vanillaModelSet().has(row.model)) {
          report({
            code: "vehicle.model.vanilla_collision",
            severity: "warning",
            title: `${row.modelRaw} replaces the base-game vehicle of the same name`,
            detail:
              "This is a legitimate thing to do on purpose — a replacement police cruiser is " +
              "exactly this — and it is worth being certain it was on purpose, because every " +
              "spawn of the stock vehicle anywhere on the server becomes this one. If the vehicle " +
              "was meant to be an add-on, give it a name of its own.",
            ...span,
            tags: ["vanilla"],
          });
        } else if (vanillaNames?.hasBasename(row.model, "yft")) {
          report({
            code: "vehicle.model.vanilla_collision",
            severity: "warning",
            title: `${row.modelRaw}.yft is also the name of a base-game asset`,
            detail:
              "The base game already streams an asset under this name, so this resource overrides " +
              "it everywhere. That is a normal way to ship a replacement and a surprising way to " +
              "ship an add-on — confirm which this is.",
            ...span,
            tags: ["vanilla"],
          });
        }
      }

      // Handling.
      if (row.handlingId === null) {
        report({
          code: "vehicle.handling.missing",
          severity: "error",
          title: `${row.modelRaw || "This vehicle"} names no handlingId`,
          detail:
            "With no handling entry to point at, the vehicle falls back to the game's default " +
            "physics: it will drive nothing like it was tuned to, and every value in handling.meta " +
            "is ignored. Add the handlingId that matches the handling entry written for it.",
          ...span,
        });
      } else {
        referencedHandling.add(normalizeName(row.handlingId));
        const resolved = handlingByName.has(normalizeName(row.handlingId));
        if (!resolved && handlingFiles.length > 0) {
          report({
            code: "vehicle.handling.missing",
            severity: "error",
            title: `handlingId ${row.handlingId} has no handling entry`,
            detail:
              `Nothing in this resource's handling.meta defines ${row.handlingId}, so the vehicle ` +
              "falls back to default physics — wrong mass, wrong grip, wrong brakes, and none of " +
              "the tuning that was written for it. Check the spelling against handling.meta; the " +
              "two are usually one character apart.",
            ...tagSpan("handlingId", row.handlingId),
          });
        }
      }

      // Audio.
      if (row.audioNameHash === null) {
        report({
          code: "vehicle.audio.empty",
          severity: "warning",
          title: `${row.modelRaw || "This vehicle"} has no audioNameHash`,
          detail:
            hasChild(row.element, "audioNameHash")
              ? "The tag is there and empty, so the vehicle inherits no engine, horn or siren " +
                "sound set and is silent apart from tyre noise. Name a base-game audio set such " +
                "as POLICE or SENTINEL."
              : "Without one the vehicle has no engine, horn or siren sound and is silent apart " +
                "from tyre noise. Name a base-game audio set such as POLICE or SENTINEL.",
          ...span,
        });
      } else if (!isValidAudioName(row.audioNameHash)) {
        report({
          code: "vehicle.audio.invalid_chars",
          severity: "warning",
          title: `audioNameHash ${row.audioNameHash} contains characters the game cannot use`,
          detail:
            `It contains ${listChars(invalidSpawnNameChars(row.audioNameHash))}. The audio set is ` +
            "looked up by hash exactly like a spawn name, so the lookup quietly returns nothing " +
            "and the vehicle is silent. Use only letters, digits and underscores.",
          ...tagSpan("audioNameHash", row.audioNameHash),
        });
      }

      // Display name.
      if (row.gameName === null) {
        report({
          code: "vehicle.game_name.missing",
          severity: "warning",
          title: `${row.modelRaw || "This vehicle"} has no gameName`,
          detail:
            "The vehicle shows up unnamed in the personal vehicle list, in dealership menus and " +
            "in every script that asks the game for a display name. Add a gameName and register " +
            "it with AddTextEntry.",
          ...span,
        });
      } else if (registeredLabels.size > 0) {
        const hash = joaat(row.gameName);
        if (!registeredLabels.has(hash)) {
          report({
            code: "vehicle.game_name.unresolved",
            severity: "error",
            title: `The name for ${row.modelRaw} never resolves`,
            detail:
              `vehicles.meta sets gameName to ${row.gameName}, so the game looks up ${toHex(hash)}. ` +
              `Nothing registers that key, so players see the raw text "${row.gameName}" in the ` +
              `menus instead of a name. Either add AddTextEntry('${row.gameName}', 'Your name') ` +
              "or change gameName to a label this resource registers.",
            ...tagSpan("gameName", row.gameName),
          });
        }
      }

      // `vehicleMakeName` is deliberately not checked the same way. The base
      // game defines every stock manufacturer label — BRAVADO, VAPID, DECLASSE
      // — so a make that no local AddTextEntry registers is the normal case,
      // not a fault, and a rule that fired on it would cry wolf on every
      // correct vehicle in existence. It is recorded in `skipped` instead.
      if (row.vehicleMakeName !== null) makesSeen = true;

      // Texture dictionary.
      if (row.txdName === null) {
        report({
          code: "vehicle.txd.missing",
          severity: "warning",
          title: `${row.modelRaw || "This vehicle"} names no txdName`,
          detail:
            "Nothing tells the game which texture dictionary to load with the model, so the " +
            "vehicle relies entirely on textures embedded in the .yft and any shared dictionary " +
            "it happens to find. Set txdName to the .ytd shipped with it.",
          ...span,
        });
      }

      // Stream files. Only when the caller supplied the inventory.
      if (streamNames && row.model !== "") {
        const modelFile = `${row.model}.yft`;
        if (!streamNames.has(modelFile)) {
          const hi = highLodNames(row.model, "yft").find((name) => streamNames.has(name));
          report({
            code: "vehicle.model.file_missing",
            severity: "error",
            title: `${modelFile} is not in this resource`,
            detail: hi
              ? `The vehicle declares a model and only ${hi} ships with it. The high-detail model ` +
                "is loaded up close and never on its own, so the vehicle has no model at all and " +
                `fails to spawn. Ship ${modelFile} alongside it.`
              : "The entry declares a vehicle and no model file with that name is streamed here, " +
                "so spawning it produces nothing. Check the .yft's name against modelName — they " +
                "must match exactly.",
            ...span,
          });
        }
      }
      if (streamNames && row.txdName !== null) {
        const txdFile = `${normalizeName(row.txdName)}.ytd`;
        if (!streamNames.has(txdFile)) {
          // `+hi` for dictionaries, `_hi` for models: the asymmetry is the
          // game's, and `highLodNames` is the only place that decides it.
          const hi = highLodNames(normalizeName(row.txdName), "ytd").find((name) =>
            streamNames.has(name),
          );
          report({
            code: "vehicle.txd.file_missing",
            severity: "error",
            title: `${txdFile} is not in this resource`,
            detail: hi
              ? `Only ${hi} ships here, and a high-detail dictionary is loaded on top of the base ` +
                "one rather than instead of it. Without the base dictionary the vehicle is " +
                `untextured at any distance. Ship ${txdFile} as well.`
              : "The vehicle asks for this texture dictionary and nothing here is called that, so " +
                "it spawns untextured — usually pure white or shader-default. Check the .ytd's " +
                "name against txdName.",
            ...tagSpan("txdName", row.txdName),
          });
        }
      }

      // Seat layout.
      if (row.layout !== null && !layoutNames.has(normalizeName(row.layout)) && layoutFiles.length > 0) {
        report({
          code: "vehicle.layout.missing",
          severity: "warning",
          title: `layout ${row.layout} is not defined in this resource`,
          detail:
            "This resource ships a vehiclelayouts.meta and it does not define this layout, so " +
            "either the name is misspelled — in which case seats, entry animations and weapon " +
            "positions all fall back to defaults — or the layout is one of the base game's and " +
            "this is correct. Check the spelling against vehiclelayouts.meta.",
          ...tagSpan("layout", row.layout),
          tags: ["external"],
        });
      }

      // Variations, and through them kits, sirens and lights.
      const variation = row.model === "" ? undefined : variations.get(row.model);
      if (variation) {
        vehicle.modkits = variation.kits;
        vehicle.sirenSettings = variation.sirenSettings;
        vehicle.lightSettings = variation.lightSettings;

        for (const kitName of variation.kits) {
          const key = normalizeName(kitName);
          kitUsers.set(key, [...(kitUsers.get(key) ?? []), row.model]);
          // `0_default_modkit` and friends are the sentinel for "this vehicle
          // has no kit". They are never defined in carcols and are not missing.
          if (isDefaultModkitName(kitName)) continue;
          if (kitsByName.has(key)) continue;

          const kitSpan = spanAt(
            variation.file,
            variation.source,
            tagOffset(variation.source, "Item", kitName, Math.max(0, variation.offset)),
            kitName.length,
          );
          if (carcolsFiles.length === 0) {
            report({
              code: "vehicle.modkit.unverified",
              severity: "info",
              title: `Mod kit ${kitName} could not be verified`,
              detail:
                "No carcols.meta was loaded with this resource, so the kit is either defined by " +
                "another resource or missing entirely and there is no way to tell from here. If " +
                "it is meant to ship with these files, add the carcols.meta and re-run the check.",
              ...kitSpan,
              tags: ["external"],
            });
          } else {
            report({
              code: "vehicle.modkit.missing",
              severity: "error",
              title: `Mod kit ${kitName} is not defined in carcols.meta`,
              detail:
                "carvariations.meta points the vehicle at this kit and carcols.meta does not " +
                "define it, so the vehicle reaches a mod shop with no parts at all — no bumpers, " +
                "no spoilers, no liveries. Kit names must match exactly, including the leading " +
                "number.",
              ...kitSpan,
            });
          }
        }

        const profiles: readonly (readonly [
          "sirenSettings" | "lightSettings",
          string | null,
          ReadonlySet<string>,
          ReadonlySet<string>,
        ])[] = [
          ["sirenSettings", variation.sirenSettings, sirenIds, vanillaSirenIdSet()],
          ["lightSettings", variation.lightSettings, lightIds, vanillaLightIdSet()],
        ];
        for (const [tag, value, defined, stock] of profiles) {
          if (value === null) continue;
          const key = profileIdKey(value);
          // Zero is not an id in either field — it means "none".
          if (key === "0") continue;
          if (defined.has(key)) continue;
          // A base-game profile id resolves outside this folder and is not a
          // fault; so is any id at all when no carcols was loaded to check.
          if (stock.has(key) || carcolsFiles.length === 0) continue;
          report({
            code: tag === "sirenSettings" ? "vehicle.siren.missing" : "vehicle.light.missing",
            severity: "warning",
            title: `${tag} ${value} is not defined in carcols.meta`,
            detail:
              tag === "sirenSettings"
                ? "This resource ships a carcols.meta that does not define this siren profile and " +
                  "the base game does not either, so the vehicle's lightbar has no pattern and " +
                  "its siren tones fall back to nothing. Define the profile or point at one that " +
                  "exists."
                : "This resource ships a carcols.meta that does not define these light settings " +
                  "and the base game does not either, so the vehicle's emergency lights use " +
                  "default corona behaviour. Define the setting or point at one that exists.",
            ...spanAt(
              variation.file,
              variation.source,
              tagOffset(variation.source, tag, value, Math.max(0, variation.offset)),
              value.length,
            ),
          });
        }
      } else if (carvariationFiles.length > 0 && row.model !== "") {
        report({
          code: "vehicle.variation.orphan",
          severity: "warning",
          title: `No carvariations entry for ${row.modelRaw}`,
          detail:
            "This resource ships a carvariations.meta and this vehicle is not in it, so it spawns " +
            "with default colours and no mod kit — the mod shop shows nothing for it. Add an " +
            "entry whose modelName matches vehicles.meta exactly.",
          file: carvariationFiles[0].file,
          entity: { kind: "vehicle", id: row.model },
        });
      }

      vehicle.findings = sortFindings(vehicle.findings);
    }
  }

  // ── Handling entries nothing points at ────────────────────────────────────

  if (vehicleFiles.length > 0) {
    const reported = new Set<string>();
    for (const entry of handlingEntries) {
      if (entry.name === "" || referencedHandling.has(entry.name)) continue;
      if (reported.has(entry.name)) continue;
      reported.add(entry.name);
      findings.push({
        code: "vehicle.handling.unused",
        severity: "warning",
        title: `No vehicle uses the handling entry ${entry.name}`,
        detail:
          "Nothing in this resource's vehicles.meta has a handlingId pointing here. Either a " +
          "vehicle's handlingId is misspelled and that vehicle is silently driving on default " +
          "physics, or this entry is left over from an earlier version and can go.",
        ...spanAt(entry.file, entry.source, entry.offset, entry.name.length),
        entity: { kind: "vehicle", id: entry.name },
      });
    }
  }

  // ── Variations with no vehicle ────────────────────────────────────────────

  if (vehicleFiles.length > 0) {
    for (const variation of variations.values()) {
      if (seenModels.has(variation.model)) continue;
      findings.push({
        code: "vehicle.variation.orphan",
        severity: "warning",
        title: `carvariations names ${variation.model} and vehicles.meta does not`,
        detail:
          "No vehicle in this resource is called that, so the colours and mod kits written here " +
          "are never applied to anything. Either the modelName is misspelled — the vehicle it was " +
          "meant for then spawns with no kit — or this entry is left over and can go.",
        ...spanAt(variation.file, variation.source, variation.offset, variation.model.length),
        entity: { kind: "vehicle", id: variation.model },
      });
    }
  }

  // ── Mod kit ids ───────────────────────────────────────────────────────────

  const byId = new Map<number, KitRecord[]>();
  for (const kit of kitRecords) {
    // A default kit name is the sentinel, not an allocation, so it is not
    // reported as colliding with the stock kit it names.
    if (kit.id === null || isDefaultModkitName(kit.kitName)) continue;
    byId.set(kit.id, [...(byId.get(kit.id) ?? []), kit]);
  }

  for (const [id, kits] of byId) {
    const users = kits.flatMap((kit) => kitUsers.get(kit.key) ?? []);
    const emergency =
      isEmergencyStockModkitId(id) || users.some((model) => isEmergencyVehicleName(model).emergency);
    const inferred = kits.every((kit) => kit.idInferred);
    const tags: GtaFindingTag[] = [];
    if (emergency) tags.push("emergency-impact");
    if (inferred) tags.push("inferred");
    const affected =
      users.length > 0
        ? `${[...new Set(users)].join(", ")} lose their mods`
        : "any vehicle using it loses its mods";

    if (kits.length > 1) {
      findings.push({
        code: "vehicle.modkit.duplicate_id",
        severity: "error",
        title: `Mod kit id ${id} is used by ${kits.length} kits`,
        detail:
          `${kits.map((kit) => kit.kitName).join(", ")} all claim id ${id}. The game indexes kits ` +
          `by id, so only one of them survives and ${affected}. Give each kit an id of its own, ` +
          "high in the range and away from the base game's.",
        ...spanAt(kits[1].file, kits[1].source, kits[1].offset, kits[1].kitName.length),
        entity: { kind: "modkit", id: String(id) },
        tags: tags.length > 0 ? tags : undefined,
      });
    }

    if (isStockModkitId(id)) {
      findings.push({
        code: "vehicle.modkit.stock_id",
        severity: inferred ? "warning" : "error",
        title: `Mod kit ${kits[0].kitName} uses stock id ${id}`,
        detail:
          `The base game already owns mod kit id ${id}. Two kits cannot share an id: whichever ` +
          `loads second is discarded, so ${affected}, and the stock vehicles on that id can lose ` +
          `theirs instead.${emergency ? " The stock kits on this id belong to the emergency fleet, so the loss is fleet-wide." : ""}` +
          ` Allocate a high id no stock kit uses.${inferred ? " This id was read from the kit's name because the kit states none, so confirm it before changing anything." : ""}`,
        ...spanAt(kits[0].file, kits[0].source, kits[0].offset, kits[0].kitName.length),
        entity: { kind: "modkit", id: String(id) },
        tags: tags.length > 0 ? tags : undefined,
      });
    }
  }

  return { vehicles, findings: sortFindings(findings), skipped };
}
